import { registerLocaleData } from '@angular/common'
import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import {
  LanguageService,
  SiteContextActions,
  SiteContextConfig,
  StateWithSiteContext,
  WindowRef
} from '@spartacus/core'
import { BehaviorSubject, combineLatest, of } from 'rxjs'
import { distinctUntilChanged, filter, map, mergeMap, switchMap, take } from 'rxjs/operators'
import { EbLocalStorageKey } from '../../../configuration/models/eb-local-storage.model'
import { EbSiteContextFallbackValues } from '../interfaces/eb-site-context.interface'
import { EbBaseSiteService } from './eb-base-site.service'

@Injectable({ providedIn: 'root' })
export class EbLanguageService extends LanguageService {
  private readonly ebLocalStorage: Storage
  private readonly isRegisteringSubject = new BehaviorSubject(true)
  private readonly registeredLocales = []
  private initialized = false

  constructor(
    protected store: Store<StateWithSiteContext>,
    protected config: SiteContextConfig,
    protected winRef: WindowRef,
    protected ebBaseSiteService: EbBaseSiteService
  ) {
    super(store, config)
    this.ebLocalStorage = winRef.nativeWindow.localStorage
  }

  isInitialized(): boolean {
    if (!this.initialized) {
      this.initialized = true
      this.validateAndSetLanguage()
    }
    return true
  }

  validateAndSetLanguage() {
    let language = EbSiteContextFallbackValues.LANGUAGE as string
    const storedLang = JSON.parse(this.ebLocalStorage.getItem(EbLocalStorageKey.LANG_KEY))

    this.setActive(this.checkForStoredLanguageOrFallback())
    this.ebBaseSiteService
      .getActiveContext()
      .pipe(
        distinctUntilChanged(),
        filter(activeContext => !!activeContext),
        switchMap(activeContext => activeContext.languages.map(lang => lang.isocode)),
        switchMap(supportedLanguages =>
          combineLatest([of(supportedLanguages), this.getActive()]).pipe(take(1))
        ),
        map(([supportedLanguages, activeLanguage]) => {
          const urlLanguage = this.getLanguageFromUrlOrStorage()
          if (supportedLanguages.includes(urlLanguage)) {
            language = urlLanguage
            if (language !== activeLanguage) {
              this.setActive(language)
            }
          } else if (supportedLanguages.includes(storedLang)) {
            language = storedLang
            if (language !== activeLanguage) {
              this.setActive(language)
            }
          }
          return language
        }),
        mergeMap(lang => this.ensureDynamicallyRegisteredLocalization(lang))
      )
      .subscribe()

    this.getActive().subscribe(activeLang =>
      this.ensureDynamicallyRegisteredLocalization(activeLang)
    )
  }

  private async ensureDynamicallyRegisteredLocalization(language): Promise<void> {
    if (!this.registeredLocales.includes(language)) {
      this.registeredLocales.push(language)
      await this.registerDynamicLocalization(language)
    }

    return new Promise(resolve => resolve())
  }

  private async registerDynamicLocalization(language) {
    this.isRegisteringSubject.next(true)
    const loadedLanguage = await import(
      /* webpackInclude: /locales.[a-z]{2}\.mjs$/ */
      /* webpackChunkName: '[request]' */
      `../../../../../node_modules/@angular/common/locales/${language}.mjs`
      )
    registerLocaleData(loadedLanguage.default)

    this.isRegisteringSubject.next(false)
  }


  getLanguageFromUrlOrStorage(): string {
    const urlParts = this.winRef.location.href.split('/')
    // verify that first parameter is existing and a country (upper case) and not a language(lower case)
    if (urlParts[4] && urlParts[4] === urlParts[4].toLowerCase() && urlParts[4].length === 2) {
      return urlParts[4]
    } else {
      // fallback
      return this.checkForStoredLanguageOrFallback()
    }
  }

  setActive(isocode: string): void {
    // ignore same languages, do always activate current language
    this.store.dispatch(new SiteContextActions.SetActiveLanguage(isocode))
  }

  checkForStoredLanguageOrFallback(): string {
    const storedLang = JSON.parse(
      this.winRef.nativeWindow.localStorage.getItem(EbLocalStorageKey.LANG_KEY)
    )
    return Object.keys(storedLang || {}).length > 0 ? storedLang : 'en'
  }
}
